/**
 * MiniSparkline — 12-point inline SVG sparkline for KpiTile.
 * Hand-rolled to avoid bringing a charting lib into the bundle just for
 * dot-plots; matches the same pattern as
 * QualityMetricsDashboard/TrendSparkline (drafts/42 polish doc).
 */

import { useMemo } from 'react';

export interface MiniSparklineProps {
  points: number[];
  /** Stroke colour. Default reads from the parent's tenant-primary token. */
  stroke?: string;
  width?: number;
  height?: number;
  ariaLabel?: string;
}

export function MiniSparkline({
  points,
  stroke = 'currentColor',
  width = 96,
  height = 28,
  ariaLabel,
}: MiniSparklineProps) {
  const { d, last, lastX, lastY } = useMemo(() => {
    if (!points.length) return { d: '', last: 0, lastX: 0, lastY: 0 };
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max === min ? 1 : max - min;
    const stepX = width / Math.max(1, points.length - 1);
    const coords: Array<[number, number]> = points.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * (height - 2) - 1;
      return [x, y];
    });
    const path = coords.map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`)).join(' ');
    const tail = coords[coords.length - 1] ?? [0, 0];
    return { d: path, last: points[points.length - 1] ?? 0, lastX: tail[0], lastY: tail[1] };
  }, [points, width, height]);

  const summary = ariaLabel ?? `Trailing ${points.length} buckets, last value ${last}`;
  return (
    <svg width={width} height={height} role="img" aria-label={summary} style={{ overflow: 'visible' }}>
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={2.5} fill={stroke} />
    </svg>
  );
}
